import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { PulseLayout, C } from "./PulseLayout";
import { showReviewNotes } from "./legalVersions";

export const Pending = ({ tag, children }) =>
  showReviewNotes() ? (
    <div className="my-4 rounded-xl border-l-4 px-4 py-3 text-sm" style={{ borderColor: "#F5B942", background: "rgba(245,185,66,0.08)", color: "#F5D899" }} data-testid="pulse-pending-item">
      {tag && <span className="mr-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold tracking-wider" style={{ background: "rgba(245,185,66,0.2)" }} data-testid="pulse-pending-tag">[{tag}] </span>}
      <span className="font-semibold">Internal review note: </span>{children}
    </div>
  ) : null;

export const P = ({ children }) => <p className="mb-4 leading-relaxed" style={{ color: "#D5DEE7" }}>{children}</p>;
export const UL = ({ items }) => (
  <ul className="mb-4 space-y-2 list-disc pl-5" style={{ color: "#D5DEE7" }}>{items.map((t, i) => <li key={i} className="leading-relaxed">{t}</li>)}</ul>
);
export const Table = ({ head, rows, testid }) => (
  <div className="mb-6 overflow-x-auto rounded-xl border" style={{ borderColor: C.slate }} data-testid={testid}>
    <table className="w-full text-sm min-w-[560px]">
      <thead style={{ background: C.ocean }}><tr>{head.map(h => <th key={h} className="text-left px-4 py-3 font-semibold" style={{ color: C.white }}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t" style={{ borderColor: C.slate }}>{r.map((c, j) => <td key={j} className="px-4 py-3 align-top" style={{ color: "#D5DEE7" }}>{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);
export const Mail = ({ to }) => <a href={`mailto:${to}`} className="underline underline-offset-2" style={{ color: C.cyan }}>{to}</a>;

const Toc = ({ sections }) => (
  <nav className="hidden lg:block sticky top-24 self-start text-sm space-y-2" aria-label="Contents" data-testid="pulse-legal-toc">
    <p className="text-xs uppercase tracking-widest mb-3" style={{ color: C.teal }}>Contents</p>
    {sections.map((s, i) => <a key={s.id} href={`#${s.id}`} className="block hover:text-white" style={{ color: C.silver }}>{i + 1}. {s.title}</a>)}
  </nav>
);

export const LegalPage = ({ title, intro, sections, other, testid, doc }) => (
  <PulseLayout>
    <div className="max-w-6xl mx-auto px-5 pt-12 md:pt-16" data-testid={testid}>
      {showReviewNotes() && (
        <div className="mb-8 rounded-2xl border px-5 py-4 flex gap-3 text-sm" style={{ borderColor: "#F5B942", background: "rgba(245,185,66,0.08)", color: "#F5D899" }} data-testid="pulse-review-banner">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          <p><b>Internal review view (development only).</b> Status: {doc.status}, version {doc.version}. Not for publication.</p>
        </div>
      )}
      <p className="text-xs tracking-[0.3em] uppercase mb-3" style={{ color: C.teal }}>PULSE by Veyronix</p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-3" data-testid={`${testid}-title`}>{title}</h1>
      <dl className="text-sm mb-6 flex flex-wrap gap-x-6 gap-y-1" style={{ color: C.silver }} data-testid={`${testid}-version`}>
        {doc.status === "final" && <div className="flex gap-1.5"><dt>Version:</dt><dd style={{ color: C.white }}>{doc.version}</dd></div>}
        <div className="flex gap-1.5"><dt>Last updated:</dt><dd style={{ color: C.white }}>{doc.lastUpdated}</dd></div>
        {doc.effectiveDate && <div className="flex gap-1.5"><dt>Effective date:</dt><dd style={{ color: C.white }}>{doc.effectiveDate}</dd></div>}
      </dl>
      <div className="max-w-3xl">{intro}</div>
      <div className="grid lg:grid-cols-[220px_1fr] gap-12 mt-10">
        <Toc sections={sections} />
        <article className="max-w-3xl min-w-0">
          {sections.map((s, i) => (
            <section key={s.id} id={s.id} className="mb-10 scroll-mt-24">
              <h2 className="font-heading text-base md:text-lg font-semibold mb-4" style={{ color: C.white }}>{i + 1}. {s.title}</h2>
              {s.body}
            </section>
          ))}
          <div className="mt-12 rounded-2xl border p-5 text-sm" style={{ borderColor: C.slate, background: C.ocean, color: C.silver }}>
            {doc.archive.length > 0 && <p className="mb-2">Previous versions: {doc.archive.map((a, i) => <span key={a.version}>{i > 0 && " · "}<a href={a.url} className="underline" style={{ color: C.cyan }}>Version {a.version} (effective {a.effectiveDate})</a></span>)}</p>}
            See also: <Link to={other.to} className="underline" style={{ color: C.cyan }} data-testid={`${testid}-cross-link`}>{other.label}</Link> · <Link to="/pulse" className="underline" style={{ color: C.cyan }}>PULSE home</Link>
          </div>
        </article>
      </div>
    </div>
  </PulseLayout>
);
